import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { trackSeoPageview, type SeoPageviewProps } from "@/lib/analytics";

const SITE_URL = "https://troopn.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/troopn-logo.png`;
const SITE_NAME = "TROOPN";

export interface PageMetaProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  type?: "website" | "article" | "profile";
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  track?: SeoPageviewProps;
}

function absUrl(path?: string) {
  if (!path) {
    if (typeof window !== "undefined") {
      return SITE_URL + window.location.pathname;
    }
    return SITE_URL + "/";
  }
  if (path.startsWith("http")) return path;
  return SITE_URL + (path.startsWith("/") ? path : "/" + path);
}

export function PageMeta({
  title,
  description,
  path,
  image,
  type = "website",
  noindex = false,
  jsonLd,
  track,
}: PageMetaProps) {
  const trackKey = track
    ? `${track.pageType}|${track.entityId ?? ""}|${track.entitySlug ?? ""}`
    : null;
  useEffect(() => {
    if (!track || noindex) return;
    trackSeoPageview(track);
  }, [trackKey, noindex]);

  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = absUrl(path);
  const ogImage = image || DEFAULT_OG_IMAGE;
  const robots = noindex ? "noindex, nofollow" : "index, follow";

  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {jsonLdArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

export default PageMeta;
